/**
 * SFCC/OCAPI credentials configuration.
 *
 * Credentials are set at runtime from Amplience installation params
 * or fall back to environment variables for local development.
 */

export interface SfccCredentials {
    readonly clientId: string
    readonly clientSecret: string
    readonly siteId?: string
}

/**
 * Runtime credentials storage.
 * Set by Home component after loading from server or Amplience SDK initialization.
 */
let credentials: SfccCredentials | null = null

/**
 * Runtime environment variables from server loader.
 * Set by Home component before SDK initialization.
 */
let runtimeEnv: { clientId?: string; clientSecret?: string; siteId?: string } | null = null

/**
 * Set runtime environment variables from server loader.
 * This should be called before initializing the SDK.
 */
export function setRuntimeEnv(env: {
    clientId?: string
    clientSecret?: string
    siteId?: string
}): void {
    runtimeEnv = env
}

/**
 * Set SFCC credentials from Amplience installation params.
 */
export function setCredentials(creds: SfccCredentials): void {
    credentials = creds
}

/**
 * Get current SFCC credentials.
 * Falls back to runtime environment variables from server, then build-time env vars.
 */
export function getCredentials(): SfccCredentials {
    if (credentials) return credentials

    // Try runtime environment variables from server loader (managed runtime)
    let clientId = runtimeEnv?.clientId
    let clientSecret = runtimeEnv?.clientSecret
    let siteId = runtimeEnv?.siteId

    // Fallback to build-time environment variables for local development
    if (!clientId) clientId = import.meta.env.VITE_OCAPI_CLIENT_ID
    if (!clientSecret) clientSecret = import.meta.env.VITE_OCAPI_CLIENT_SECRET
    if (!siteId) siteId = import.meta.env.VITE_SITE_ID

    if (!clientId || !clientSecret) {
        console.warn(
            'SFCC credentials not configured. Set via Amplience installation params, ' +
                'instance environment variables (OCAPI_CLIENT_ID/OCAPI_CLIENT_SECRET), ' +
                'or local .env.local (VITE_OCAPI_CLIENT_ID/VITE_OCAPI_CLIENT_SECRET).'
        )
    }

    return {
        clientId: clientId ?? '',
        clientSecret: clientSecret ?? '',
        siteId
    } satisfies SfccCredentials
}

/**
 * Check if credentials are configured.
 */
export function hasCredentials(): boolean {
    const creds = getCredentials()
    return Boolean(creds.clientId && creds.clientSecret)
}
